import axios from 'axios'
import { useState } from 'react'
import { apiStatus } from '../constants/apiStatus'
import { axiosDefaultHeader, baseUrl, stage, apiUrl } from '../constants/apiUrl'
import { loadStatus } from '../constants/loadStatus'
import { errorMsg } from '../constants/messages'
import type { ResultIllustrationInfo } from '../interfaces/IllustrationIf'
import { useAuth } from './useAuth'
import { useApiUrl } from './useApiUrl'

interface C504IllustrationUpdateProps {
  execIllustrationUpdate: (
    deviceId: string,
    oldIllustrationName: string,
    newIllustrationName: string,
    file_body?: File,
    full_file_body?: File,
    signed_url?: string
  ) => Promise<ResultIllustrationInfo>
  loading: loadStatus
}

// C504 図解更新API呼出（Put /devices/{deviceId}/illustrations）
export const useC504IllustrationUpdate = (): C504IllustrationUpdateProps => {
  const [loading, setLoading] = useState(loadStatus.NONE)
  const auth = useAuth()
  const { buildDeviceUrl } = useApiUrl() 
  const execIllustrationUpdate = async (
    deviceId: string,
    oldIllustrationName: string,
    newIllustrationName: string,
    file_body?: File,
    full_file_body?: File,
    signed_url?: string
  ): Promise<ResultIllustrationInfo> => {
    try {
      setLoading(loadStatus.LOADING)
      const jwt = await auth.getJwt()
      let body = {}
      if (jwt === '') {
        setLoading(loadStatus.LOADED)
        return { success: false, message: errorMsg.SESSION_ERROR }
      }
      // ヘッダ作成
      const header = Object.assign(axiosDefaultHeader, {
        Authorization: 'Bearer ' + jwt,
      })

      if (file_body) {
        body = {
          old_illustration_name: oldIllustrationName,
          illustration_name: newIllustrationName,
          language: 'ja',
          file_name: file_body.name,
          url: signed_url,
        }
      } else if (full_file_body) {
        body = {
          old_illustration_name: oldIllustrationName,
          illustration_name: newIllustrationName,
          language: 'ja',
          full_file_name: full_file_body.name,
          full_url: signed_url,
        }
      } else {
        body = { old_illustration_name: oldIllustrationName, illustration_name: newIllustrationName, language: 'ja' }
      }
      const response = await axios.put(
        buildDeviceUrl(deviceId, apiUrl.illustrationsPath),
        body,
        {
          headers: header,
          validateStatus: () => true,
        },
      )

      setLoading(loadStatus.LOADED)

      if (response.status === apiStatus.SUCCESS) {
        return { success: true, info: response.data }
      }
      if (response.data && response.data.message) {
        return { success: false, message: response.data.message }
      }
      return { success: false, message: errorMsg.SYSTEM_ERROR }
    } catch {
      setLoading(loadStatus.LOADED)
      return { success: false, message: errorMsg.SYSTEM_ERROR }
    }
  }

  return { execIllustrationUpdate, loading }
}